/**
 * Docs Service — the BP-compat document surface
 * (`/api/v1/application/{loanId}/bp-documents*`).
 *
 * Deliberately NOT the wizard's `/documents*` trio, which `services/documents.ts`
 * already calls: the two read the same Vesta documents but the wizard's model
 * drops the category and expiry fields this page's grouping and badges are built
 * on. afn-path-api serves both surfaces side by side for exactly that reason.
 *
 * Three wire quirks, all reproduced from the old backend rather than corrected,
 * because the API reproduces them too:
 *  - the list is a BARE ARRAY, not `{ documents: [...] }`;
 *  - the upload takes a BARE ARRAY body and reports per-file failure IN BAND —
 *    HTTP 200 with the error text sitting where an id should be (see
 *    {@link readUploadedId});
 *  - the download RESPONSE is PascalCase (`{ Name, Type, Content }`) while its
 *    request is camelCase.
 */

import { apiClient } from './api-client';
import { fileToBase64, toNumberOrUndefined } from './documents';
import type {
  BpDocument,
  BpDownloadDocumentRequest,
  BpDownloadDocumentResponse,
  BpUploadDocumentItem,
  BpUploadDocumentsResponse,
  DocBorrower,
  DocsDocument,
} from '@/types/docs';

const BASE = '/api/v1/application';

/**
 * The document type the old app filed every "Upload Any Documents Here" file
 * under. The API's projection turns it into a `"Other Uploads - <file>"` title,
 * which this page relabels to "Uncategorized" — so this constant is what puts an
 * ad-hoc upload in the right section.
 *
 * Exported so `DocumentsList`'s per-borrower filter row can ask
 * `useDocumentAssociationOptions` for the same document type existing
 * uploads are filed under — the two must never drift apart, or the filter's
 * borrower roster stops matching what documents are actually grouped under.
 */
export const UNCATEGORIZED = 'Uncategorized';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * BP's upload endpoints answer 200 even when a file failed, writing the failure
 * message into the same array slot the id would occupy — so the only way to tell
 * the two apart is that a success parses as a GUID. Returns the id, or throws the
 * message the API put there.
 *
 * Shared with the task-card upload in `services/tasks.ts`, which uses the same
 * in-band convention on a different route.
 */
export function readUploadedId(value: string | null | undefined, fallback: string): string {
  const id = (value ?? '').trim();
  if (UUID.test(id)) return id;
  throw new Error(id || fallback);
}

/** The wire shape of one entry in `associatedBorrowers`, until api.gen.ts knows it. */
type RawDocBorrower = { entityId?: string | null; firstName?: string | null; lastName?: string | null };

/**
 * NP-611 AC-1/AC-2 — Vesta normalizes an uploaded photo to a PDF on ingest and stores
 * the ORIGINAL name with `.pdf` appended rather than replaced: a borrower's `id.jpg`
 * comes back as `id.jpg.pdf` (see the identical note on `useTaskAttachmentFile` in
 * `useTasks.ts`, which hits the same Vesta behavior on the Tasks side). The upload was
 * never named that on the way in, so the redundant suffix is dropped wherever the
 * original extension still shows through it. A genuine `statement.pdf` — no second
 * extension in front of it — is untouched.
 */
const REDUNDANT_PDF_SUFFIX = /\.(jpe?g|png)\.pdf$/i;

function displayFileName(name: string): string {
  return name.replace(REDUNDANT_PDF_SUFFIX, (_match, ext: string) => `.${ext}`);
}

function toDocBorrower(borrower: RawDocBorrower): DocBorrower;
function toDocBorrower(borrower: RawDocBorrower | null | undefined): DocBorrower | null;
function toDocBorrower(borrower: RawDocBorrower | null | undefined): DocBorrower | null {
  if (!borrower) return null;
  return {
    entityId: borrower.entityId ?? '',
    firstName: borrower.firstName ?? '',
    lastName: borrower.lastName ?? '',
  };
}

/** The wire shape → what the Docs page renders. Exported for fixtures and tests. */
export function normalizeDocument(doc: BpDocument): DocsDocument {
  return {
    id: doc.id ?? '',
    title: displayFileName(doc.title ?? ''),
    description: doc.description ?? '',
    status: doc.status ?? '',
    statusDate: doc.statusDate ?? null,
    receivedDate: doc.receivedDate ?? null,
    documentGroups: doc.documentGroups ?? [],
    // Read off the wire rather than off `BpDocument`: `gen:api-types` generates from
    // the deployed dev API, which does not serve `associatedBorrowers` until
    // afn-path-api#187 ships. The cast goes at the regeneration after that deploy.
    associatedBorrowers: ((doc as BpDocument & { associatedBorrowers?: RawDocBorrower[] }).associatedBorrowers ?? [])
      .map(toDocBorrower),
    // NP-608: absent for anything PATH did not upload, and for every document
    // uploaded before the API recorded it — both mean "nobody to name", not "nobody
    // uploaded it". Cast for the same reason as above, until the next regeneration.
    uploadedByBorrower: toDocBorrower(
      (doc as BpDocument & { uploadedByBorrower?: RawDocBorrower | null }).uploadedByBorrower,
    ),
    // NP-608 AC-13: cast for the same reason as above, until the next regeneration.
    uploadedByCurrentBorrower:
      (doc as BpDocument & { uploadedByCurrentBorrower?: boolean }).uploadedByCurrentBorrower ?? false,
    attachments: (doc.attachments ?? []).map((attachment) => ({
      fileSize: toNumberOrUndefined(attachment.fileSize) ?? null,
      entityId: attachment.entityId ?? '',
      entityName: displayFileName(attachment.entityName ?? ''),
      entityType: attachment.entityType ?? 'Default',
    })),
    isMarkedRemoved: doc.isMarkedRemoved ?? false,
    daysTillExpire: toNumberOrUndefined(doc.daysTillExpire) ?? 0,
  };
}

export const docsService = {
  async getDocuments(loanId: string): Promise<DocsDocument[]> {
    const documents = await apiClient.get<BpDocument[]>(`${BASE}/${loanId}/bp-documents`);
    return (documents ?? []).map(normalizeDocument);
  },

  /**
   * The old app's generic "Upload Any Documents Here" flow: everything is filed
   * as {@link UNCATEGORIZED} and staff re-title and re-group it later. Returns
   * the new document's id.
   *
   * No `associatedEntityId` — briefly added (afn-path-api#188), and reverted
   * live: Vesta's own upload-time validation rejects a Borrower entity for
   * this document type ("Invalid associated entity {id} for document type
   * Uncategorized"), so the upload always files against the loan and this
   * function has no way to change that.
   */
  async uploadDocument(loanId: string, file: File, signal?: AbortSignal): Promise<string> {
    const items: BpUploadDocumentItem[] = [
      {
        fileName: file.name,
        fileContent: await fileToBase64(file),
        documentType: UNCATEGORIZED,
      },
    ];
    const response = await apiClient.post<BpUploadDocumentsResponse>(
      `${BASE}/${loanId}/bp-documents/upload`,
      items,
      { signal },
    );
    return readUploadedId(response?.fileIds?.[0], `Upload failed for "${file.name}".`);
  },

  /**
   * One file's bytes, base64, with its real MIME type in `Type`.
   *
   * `isThumbnail` is the caller's choice, and the API rasterises: page 1 of a PDF
   * comes back as a PNG (PDFium + SkiaSharp behind `DownloadBpDocumentHandler`),
   * while a non-PDF returns its own bytes and type. A file it cannot render —
   * corrupt, password protected, no rasteriser on the host — answers with
   * `Content: null`, which is the card's icon case rather than an error.
   */
  downloadFile(
    loanId: string,
    request: BpDownloadDocumentRequest,
    opts?: { signal?: AbortSignal },
  ): Promise<BpDownloadDocumentResponse> {
    return apiClient.post<BpDownloadDocumentResponse>(`${BASE}/${loanId}/bp-documents/download`, request, opts);
  },
};
